import { MenuItem, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '../common/dialog'
import { Pocket } from '../../utils/dexie/db'
import { countriesCurrencies } from '../../lib'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { parseNumber } from '../customer-companies-management/utils'

export const TransferPocketModal = ({
  isLoading,
  selectedPocketId,
  currentPocketsData,
  handleTransferPocket,
  handleOpenStateOfModal
}: {
  isLoading: boolean
  selectedPocketId: string
  currentPocketsData: Array<Pocket>
  handleOpenStateOfModal: (openState: boolean) => void
  handleTransferPocket: (selectedPocketId: string, pocketToTransferId: string, amountToTransfer: number) => void
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toUpperCase()

  const { t } = useTranslation(['pockets-management'])

  const [pocketToTransferId, setPocketToTransferId] = useState<string>('')
  const [pocketsToSelect, setPocketsToSelect] = useState<Array<Pocket>>([])
  const [amountToTransferValue, setAmountToTransferValue] = useState<string>('')
  const [selectedPocketFromTransfer, setSelectedPocketFromTransfer] = useState<Pocket | null>(null)
  const [errors, setErrors] = useState<{ amountToTransfer: string; pocketToTransferId: string } | null>(null)

  useEffect(() => {
    setSelectedPocketFromTransfer(currentPocketsData.find((pocketData: Pocket) => String(pocketData.id) === String(selectedPocketId)) as Pocket)
    setPocketsToSelect(currentPocketsData.filter((pocketData: Pocket) => String(pocketData.id) !== String(selectedPocketId)) as Array<Pocket>)
  }, [selectedPocketId, currentPocketsData])

  const handleSetMaximumToTransfer = () => {
    const amountToTransferValueToSet = handleGetAmountToTransferValue(String(Number(selectedPocketFromTransfer?.balance) - Number(selectedPocketFromTransfer?.balanceToBeDebited)))
    setAmountToTransferValue(`$ ${amountToTransferValueToSet}`)
  }

  const handleSubmitPocketDataTransfer = () => {
    if (isLoading) return

    setErrors(null)
    const errorsToSet = {
      amountToTransfer: '',
      pocketToTransferId: ''
    }
    let formDataIsValid = true

    if (!amountToTransferValue) {
      formDataIsValid = false
      errorsToSet.amountToTransfer = t('transferPocketModal.errors.pocketTransfer')
    }

    if (amountToTransferValue) {
      const amountToTransferNumber = amountToTransferValue.replaceAll('$', '').replaceAll(',', '').replaceAll('.', '').trim()

      if (Number(amountToTransferNumber) <= 0) {
        formDataIsValid = false
        errorsToSet.amountToTransfer = t('transferPocketModal.errors.pocketTransferZero')
      }

      if (Number(amountToTransferNumber) > Number(selectedPocketFromTransfer?.balance)) {
        formDataIsValid = false
        const maximumAmountToTransfeAllowed = handleGetAmountToTransferValue(String(selectedPocketFromTransfer?.balance))
        errorsToSet.amountToTransfer = `${t('transferPocketModal.errors.pocketTransferMaximum')} $ ${maximumAmountToTransfeAllowed} ${currency}`
      }
    }

    if (!pocketToTransferId) {
      formDataIsValid = false
      errorsToSet.pocketToTransferId = t('transferPocketModal.errors.pocketToTransfer')
    }

    if (!formDataIsValid) {
      setErrors({ ...errorsToSet })
      return
    }

    const amountToTransferValueNumber = Number(amountToTransferValue.replaceAll('$', '').replaceAll(',', '').replaceAll('.', '').trim())
    handleTransferPocket(selectedPocketId, pocketToTransferId, amountToTransferValueNumber)
  }

  const handleGetAmountToTransferValue = (valueToSet: string) => {
    const extractedNumberOfValueToSet = valueToSet.replaceAll('$', '').replaceAll(',', '').replaceAll('.', '').trim()

    if (isNaN(Number(extractedNumberOfValueToSet))) return ''

    if (extractedNumberOfValueToSet === '') return ''

    return parseNumber(extractedNumberOfValueToSet)
  }

  return (
    <Dialog
      dialogIsLoading={isLoading}
      dialogTitle={t('transferPocketModal.title')}
      handleDialogAction={handleSubmitPocketDataTransfer}
      actionButtonText={t('transferPocketModal.actionButtonText')}
      handleDialogOpenState={(openState: boolean) => {
        if (isLoading) return

        handleOpenStateOfModal(openState)

        if (openState === false) {
          setPocketToTransferId('')
          setSelectedPocketFromTransfer(null)
        }
      }}
    >
      <div
        style={{ textAlign: 'center', lineHeight: '150%' }}
        className="company-customer-funding-modal-main-instruction"
      >{`${t('transferPocketModal.mainInstruction')} ${selectedPocketFromTransfer?.name}:`}</div>
      <div className="company-customer-funding-content-container">
        <div className="trade-module-container">
          <div className="operation-container">
            <div
              className="operation-title"
              style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
            >
              <div>{t('transferPocketModal.amountToTransfer')}:</div>
              <div
                onClick={handleSetMaximumToTransfer}
                style={{ fontWeight: '700', color: 'rgb(46, 211, 183)', cursor: 'pointer' }}
              >
                MAX
              </div>
            </div>
            <div className="operation-calculation-container">
              <div className="operation-text-field-container">
                <TextField
                  fullWidth
                  placeholder="$0"
                  variant="standard"
                  sx={textFieldWhiteBg}
                  error={!!errors?.amountToTransfer}
                  helperText={errors?.amountToTransfer}
                  value={amountToTransferValue || ''}
                  onBlur={() => {
                    if (amountToTransferValue.slice(-1) === '.') {
                      setAmountToTransferValue(amountToTransferValue.slice(0, -1))
                    }
                  }}
                  onChange={(event) => {
                    const amountToTransferValueToSet = handleGetAmountToTransferValue(String(event.target.value))

                    if (amountToTransferValueToSet === '') {
                      setAmountToTransferValue(amountToTransferValueToSet)
                      return
                    }

                    setAmountToTransferValue(`$ ${amountToTransferValueToSet}`)
                  }}
                />
              </div>
              <div className="operation-currency-data-container">{currency}</div>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{ marginTop: '20px' }}
        className="company-customer-funding-content-container"
      >
        <div className="trade-module-container">
          <div className="operation-container">
            <div className="operation-title">{t('transferPocketModal.transferTo')}</div>
            <div className="operation-calculation-container">
              <div
                style={{ width: '100%' }}
                className="operation-text-field-container"
              >
                <TextField
                  select
                  fullWidth
                  variant="standard"
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
                  value={pocketToTransferId || ''}
                  error={!!errors?.pocketToTransferId}
                  helperText={errors?.pocketToTransferId}
                  label={t('transferPocketModal.selectLabel')}
                  onChange={(event) => setPocketToTransferId(event.target.value)}
                >
                  <MenuItem
                    disabled
                    value=""
                  >
                    {t('transferPocketModal.select')}
                  </MenuItem>
                  {pocketsToSelect.map((pocketData: Pocket, index: number) => {
                    return (
                      <MenuItem
                        key={index}
                        value={String(pocketData.id)}
                      >
                        {String(pocketData.name?.toUpperCase())}
                      </MenuItem>
                    )
                  })}
                </TextField>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
